import { useQuery } from "@hooks/useQuery";
import { Tabs } from "antd";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DynamicForm, FormConfig } from "~/Components/DynamicForm/DynamicForm";
import { useNotification } from "~/Hooks/useNotification";
import { post, put } from "~/Services";
import { magpHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { useDocumentDetails } from "../../../ApprovalManagement/Drilldown.Hooks";
import { useIntroduction } from "../Manual.hook";
import { fields } from "../manualConstants";
import { Loader } from "~/Components";

export const 
Introduction: React.FC = (): JSX.Element => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [id, setId] = useState("");
  const approveQuery = new URLSearchParams(location.search);
  const approveId = approveQuery.get("id");
  const {
    data: introductionData,
    error,
    isLoading,
    isError,
    isPending,
    refetch: refetchIntroduction,
  } = useIntroduction(id || "6");
  const {
    data: introductionDataById,
    isLoading: isLoadingIntroduction,
    isPending: isPendingByIntroduction,
    refetch: refetchData,
  } = useDocumentDetails(query?.get("id") || "");

  const introData: any[] = [
    {
      key: "preface",
      id: "6",
    },
    {
      key: "procurementCodesOfEthics",
      id: "7",
    },
    {
      key: "generalProvision",
      id: "8",
    },
    {
      key: "termsAndConditions",
      id: "9",
    },
    {
      key: "processSymbols",
      id: "10",
    },
    {
      key: "mahbProcurementValueChain",
      id: "11",
    },
  ];

  useEffect(() => {
    if (approveId) {
      return;
    }
    if (query.get("subActiveTab") === "preface") {
      setId('6');
    }
    if (query.get("subActiveTab") === "procurementCodesOfEthics") {
      setId('7');
    }
    if (query.get("subActiveTab") === "generalProvision") {
      setId('8');
    }
    if (query.get("subActiveTab") === "termsAndConditions") {
      setId('9');
    }
    if (query.get("subActiveTab") === "processSymbols") {
      setId('10');
    }
    if (query.get("subActiveTab") === "mahbProcurementValueChain") {
      setId('11');
    }
  }, [query.get("activeTab"), query.get("subActiveTab")]);

  useEffect(() => {
    refetchIntroduction();
  }, []);

  useEffect(() => {
    if (approveId) {
      refetchData();
    }
  }, [approveId]);

  useEffect(() => {
    if (id) {
      refetchIntroduction();
    }
  }, [id]);

  const IntroductionFormConfig: FormConfig = {
    initialValues: {
      title:
        introductionDataById?.payload && !isLoadingIntroduction
          ? decodeHTML(introductionDataById?.payload?.title)
          : decodeHTML(introductionData?.[0]?.title),
      field_introduction_description:
        introductionDataById?.payload && !isLoadingIntroduction
          ? decodeHTML(introductionDataById?.payload?.field_introduction_description?.value)
          : decodeHTML(introductionData?.[0]?.field_introduction_description),

      field_intro_description_malay:
        introductionDataById?.payload && !isLoadingIntroduction
          ? decodeHTML(introductionDataById?.payload?.field_intro_description_malay?.value)
          : decodeHTML(introductionData?.[0]?.field_intro_description_malay),

      field_intro_description_mandarin:
        introductionDataById?.payload && !isLoadingIntroduction
          ? decodeHTML(introductionDataById?.payload?.field_intro_description_mandarin?.value)
          : decodeHTML(introductionData?.[0]?.field_intro_description_mandarin),

      field_video_url: introductionDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "video_url.mp4",
              status: "done",
              url: introductionDataById?.payload?.field_video_url,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "video_url.mp4",
              status: "done",
              url: introductionData?.[0]?.field_video_url,
            },
          ]),

      field_intro_details_web_image:
        introductionDataById?.payload && !isLoadingIntroduction
          ? decodeHTML([
              {
                uid: "-1",
                name: "field_intro_details_web_image.png",
                status: "done",
                url: introductionDataById?.payload?.field_intro_details_web_image,
              },
            ])
          : decodeHTML([
              {
                uid: "-1",
                name: "field_intro_details_web_image.png",
                status: "done",
                url: introductionData?.[0]?.field_intro_details_web_image,
              },
            ]),
    },
    fields: fields.introduction,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    let imageKeys: any = [
      "fbImage",
      "field_purchase_login_pop_image",
      "field_login_image",
      "filledImage",
      "googleImage",
      "showImage",
      "field_logout_image",
      "field_intro_details_web_image",
      "field_video_url",
    ];

    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field && field[0]) {
          if (field[0].originFileObj) {
            const formData = new FormData();
            formData.append("upload-file", field[0].originFileObj);
            formData.append("application", "MAGP");
            const res: any = await post("management/upload-file", formData);

            if (res) {
              data[key] = res.response.data;
            }
          } else {
            data[key] = data[key][0].url;
          }
        }
      } catch (error) {}
    }

    let descKeys: any = [
      "field_introduction_description",
      "field_intro_description_malay",
      "field_intro_description_mandarin",
    ];
    for (const key of descKeys) {
      try {
        data[key] = {
          value: data[key] || "",
          format: "full_html",
        };
      } catch (error) {}
    }
    const payload = {
      action: action,
      method: method,
      module: "Manual",
      subModule: `${query?.get("activeTab")}_${query?.get("subActiveTab")}`,
      type: "DEDICATED",
      application: "MAGP",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAGP",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        // id: data?.id ? data?.id : introductionData?.[0]?.id,
        field_introduction_list_referenc: introductionData?.[0]?.field_introduction_list_referenc,
        field_introduction_toggle_refere: introductionData?.[0]?.field_introduction_toggle_refere,
        ...data,
        published: true,
        createdAt: "2024-07-16T07:44:25",
        updatedAt: "2024-07-16T07:44:30",
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          refetchIntroduction();
          refetchData();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          if (response.status === 'OK' && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else{
            navigate("/dedicated/status-information?activeTab=draft");
          }
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          refetchIntroduction();
          refetchData();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  const createIntroductionSubChild = (label: string, key: string) => ({
    label,
    key,
    children: (
      <DynamicForm
        isLoading={isLoading || isLoadingIntroduction}
        config={IntroductionFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${magpHost}/api/introduction-detail/${introductionData?.[0]?.id}`,
            "UPDATE",
            "PATCH",
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    ),
  });

  const introductionChildItems = [
    createIntroductionSubChild("Preface", "preface"),
    createIntroductionSubChild("Procurement Codes of Ethics", "procurementCodesOfEthics"),
    createIntroductionSubChild("General Provision", "generalProvision"),
    createIntroductionSubChild("Terms & Conditions", "termsAndConditions"),
    createIntroductionSubChild("Process Symbols", "processSymbols"),
    createIntroductionSubChild("MAHB Procurement Value Chain", "mahbProcurementValueChain"),
  ];

  if (isLoading || isLoadingIntroduction) {
    return <Loader />;
  }

  return (
    <Tabs
      activeKey={query?.get("subActiveTab") || ""}
      items={introductionChildItems}
      onChange={(activeKey) => {
        const key = introData.find((key) => key.key === activeKey);
        setId(key.id);
        navigate(`${window.location.pathname}?activeTab=introduction&subActiveTab=${activeKey}`);
      }}
    ></Tabs>
  );
};
